import { useState } from 'react';
import Link from 'next/link';
import Dialog from '@mui/material/Dialog';
import { AxiosError } from 'axios';
import { PostPortfolioItemType } from 'common/interfaces/api';
import { useMediaQuery } from 'common/utils';
import {
  errorMessageByCode,
  fixedNumber,
  formatCurrency,
} from 'common/utils/trading-demo';
import { CloseIcon, WarningIcon } from 'components/v2/atoms/icon';
import { Input } from 'components/v2/atoms/input';
import { useMutation, useQuery } from 'react-query';
import {
  callGetPriceInfoRequest,
  callOrderRequest,
  PositionTypeEnum,
} from 'service/trading-demo';
import useGlobalStore from 'store/global';

import { OrderSuccessPopupType } from './order-success-popup';

const BuySellDialog = ({
  currentSymbol,
  onClose,
  title,
  postId,
  refetchPortfolioInfoData,
  setSuccessPopupData,
}: {
  currentSymbol: PostPortfolioItemType | null;
  onClose: () => void;
  title: string;
  postId?: number;
  refetchPortfolioInfoData: () => void;
  setSuccessPopupData: (data: OrderSuccessPopupType | null) => void;
}) => {
  const [quantity, setQuantity] = useState(1);
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const { data: priceInfoData } = useQuery(
    [
      'priceInfoDataPostDetails',
      currentAuth?.uid?.toString(),
      currentSymbol?.symbol_id?.toString(),
    ],
    async () => {
      if (!currentAuth || !isAuthInit) return Promise.reject();
      return await callGetPriceInfoRequest({
        symbolId: currentSymbol.symbol_id,
      });
    },
    {
      enabled: !!currentAuth && isAuthInit,
      refetchOnWindowFocus: false,
    },
  );
  const virtualBalance = priceInfoData?.data?.balance;
  const jpyRate = parseFloat(priceInfoData?.data?.jpy_rate);
  const currency = priceInfoData?.data?.currency || '';
  const symbolPrice = priceInfoData?.data?.price || '';
  const symbolName = currentSymbol?.symbol_name || '';
  const { mutate: handleOrderAction, isLoading } = useMutation(
    async ({
      positionType,
      quantity,
    }: {
      positionType: PositionTypeEnum;
      quantity: number;
    }) => {
      try {
        return await callOrderRequest({
          symbolId: currentSymbol.symbol_id,
          quantity: quantity,
          positionType: positionType,
          postId: postId,
        });
      } catch (error) {
        console.error('Order request failed:', error);
        throw error;
      }
    },
    {
      onError: (error: AxiosError<{ error: { detail_code: string } }>) => {
        const errorMessage =
          errorMessageByCode(error?.response?.data?.error?.detail_code || '') ||
          '';
        setSnackbarMessage({
          type: 'error',
          text: errorMessage,
        });
      },
      onSuccess: (data, variables) => {
        // Show success popup with order details
        setSuccessPopupData({
          type: variables.positionType,
          symbol: symbolName,
          qty: variables.quantity,
          price: formatCurrency({
            value: symbolPrice,
            withSign: false,
            supportZero: false,
            currency: currency,
            isSymbolPrice: true,
          }),
        });
        onClose();
        setTimeout(() => {
          refetchPortfolioInfoData();
        }, 1000);
      },
    },
  );

  const isMobile = useMediaQuery('(max-width: 767px)');
  if (!currentSymbol) return null;

  return (
    <Dialog
      open={!!currentSymbol}
      onClose={onClose}
      maxWidth="lg"
      fullWidth={isMobile}
      scroll="body"
      PaperProps={{
        style: {
          maxHeight: 'calc(100vh - 80px)',
          borderRadius: '16px',
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.30)',
          },
        },
      }}
    >
      <div className="all-font-sans w-full rounded-lg bg-white sm:w-[400px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-pp-border-primary px-4 py-5">
          <h2 className="text-base font-bold leading-none text-pp-text-primary">
            {title}
          </h2>
          <button onClick={onClose}>
            <CloseIcon
              className="size-4 text-pp-text-secondary"
              width={16}
              height={16}
            />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Virtual Balance */}
          <div className="mb-6 rounded-lg bg-[#F8F9FA] px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-pp-text-secondary">デモ残高</span>
              <span className="text-lg font-bold text-pp-text-primary">
                {formatCurrency({
                  value: virtualBalance,
                  supportZero: false,
                  withSign: false,
                  currency: 'JPY',
                })}
              </span>
            </div>
          </div>

          {/* Symbol Info */}
          <div className="mb-6 text-center">
            <div className="mb-2 text-2xl font-bold text-pp-text-primary">
              {symbolName}
            </div>
            <div className="text-xl text-pp-text-secondary">
              {formatCurrency({
                value: symbolPrice,
                withSign: false,
                supportZero: false,
                currency: currency,
                isSymbolPrice: true,
              })}
            </div>
          </div>

          {/* Quantity Input */}
          <div className="mb-6">
            <label className="mb-2 block text-sm text-pp-text-primary">
              数量
            </label>
            <Input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-full rounded-lg border border-pp-border-primary px-4 py-3 text-center text-base font-bold leading-none focus:border-transparent focus:outline-none focus:ring-1 focus:ring-pp-text-primary"
              min="1"
              step="1"
            />
          </div>

          {/* Order Calculation - Collapsible */}
          <div className="mb-6 rounded-lg border border-pp-border-primary bg-[#F8F9FA]">
            {/* Summary Row (Always Visible) */}
            <div
              className="flex cursor-pointer items-center justify-between p-4"
              onClick={() => setShowCalculationDetails(!showCalculationDetails)}
            >
              <div className="flex w-full items-center justify-between">
                <span className="text-[15px] font-semibold text-pp-text-primary">
                  注文金額
                </span>
                <span className="text-lg font-bold text-pp-text-primary">
                  {formatCurrency({
                    value: String(parseFloat(symbolPrice) * quantity * jpyRate),
                    supportZero: false,
                    withSign: false,
                    currency: 'JPY',
                  })}
                </span>
              </div>
              <button
                type="button"
                className={`ml-2 transition-transform duration-300 ease-in-out ${
                  showCalculationDetails ? 'rotate-180' : ''
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Detailed Calculation (Collapsible) */}
            <div
              className={`overflow-hidden border-t border-pp-border-primary transition-all duration-300 ease-in-out ${
                showCalculationDetails
                  ? 'max-h-96 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <div className="space-y-3 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-pp-text-secondary">単価</span>
                  <span className="text-sm font-semibold text-pp-text-primary">
                    {formatCurrency({
                      value: symbolPrice,
                      withSign: false,
                      supportZero: false,
                      currency: currency,
                      isSymbolPrice: true,
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-pp-text-secondary">数量</span>
                  <span className="text-sm font-semibold text-pp-text-primary">
                    {quantity}
                  </span>
                </div>
                <div className="border-t border-pp-border-primary pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-pp-text-secondary">
                      小計 ({currency})
                    </span>
                    <span className="text-sm font-semibold text-pp-text-primary">
                      {formatCurrency({
                        value: String(
                          parseFloat(symbolPrice || '0') * quantity,
                        ),
                        withSign: false,
                        supportZero: false,
                        currency: currency,
                        isSymbolPrice: true,
                      })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-white px-1 py-2">
                  <span className="flex items-center gap-1 text-sm text-pp-text-secondary">
                    為替レート
                    {/* <span className="text-xs" title="リアルタイムレート">
                      ℹ️
                    </span> */}
                  </span>
                  <span className="text-sm font-semibold text-pp-text-primary">
                    1 {currency} = {fixedNumber(jpyRate, 2)} JPY
                  </span>
                </div>
                <div className="border-t border-pp-border-primary pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-pp-text-primary">
                      合計 (JPY)
                    </span>
                    <span className="text-lg font-bold text-pp-text-primary">
                      {formatCurrency({
                        value: String(
                          parseFloat(priceInfoData?.data?.price || '0') *
                            quantity *
                            jpyRate,
                        ),
                        supportZero: false,
                        withSign: false,
                        currency: 'JPY',
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div
            className="mb-6 flex items-center gap-1 rounded-xl border border-warning-orange-primary p-2.5"
            style={{
              background: 'linear-gradient(135deg, #FFF9E5 0%, #FFF3CD 100%)',
            }}
          >
            <span className="shrink-0">
              <WarningIcon color="#f59e0b" width={21} height={21} />
            </span>
            <div className="flex-1">
              <div className="text-xs text-pp-text-primary">
                これはデモトレードです。実際の資金は使用されません。
              </div>
            </div>
          </div>

          {/* Buy/Sell Buttons */}
          <div className="mb-6 flex gap-4 bg-[#F8F9FA]">
            <button
              onClick={() => {
                handleOrderAction({
                  positionType: PositionTypeEnum.Short,
                  quantity,
                });
              }}
              disabled={isLoading || quantity <= 0 || !currentSymbol?.symbol_id}
              className="flex-1 rounded-lg bg-pp-error-primary px-4 py-3 text-base font-bold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              売り
            </button>

            <button
              onClick={() => {
                handleOrderAction({
                  positionType: PositionTypeEnum.Long,
                  quantity,
                });
              }}
              disabled={isLoading || quantity <= 0 || !currentSymbol?.symbol_id}
              className="flex-1 rounded-lg bg-pp-green-primary px-4 py-3 text-base font-bold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              買い
            </button>
          </div>
          {/* Portfolio Button */}
          <div className="flex items-center justify-center">
            <Link
              href="/p/portfolio"
              onClick={() => {
                onClose();
              }}
              className="flex w-fit items-center justify-center gap-2 rounded-lg border border-pp-border-primary bg-white px-6 py-3 text-pp-text-primary transition-colors hover:bg-pp-border-primary"
            >
              <span className="text-sm font-semibold">
                ポートフォリオを見る
              </span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M5 8H11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M8 5L11 8L8 11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default BuySellDialog;
